import { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Search, SlidersHorizontal, X } from "lucide-react";

const CATEGORIES = [
  { slug: "all", label: "ALL" },
  { slug: "jackets", label: "JACKETS" },
  { slug: "tees", label: "TEES" },
  { slug: "jeans", label: "JEANS" },
  { slug: "tote-bags", label: "TOTES" },
  { slug: "tops", label: "TOPS" },
  { slug: "thrifted", label: "THRIFTED" },
];

const SORTS = [
  { key: "new", label: "NEWEST" },
  { key: "price-asc", label: "PRICE ↑" },
  { key: "price-desc", label: "PRICE ↓" },
  { key: "aesthetic", label: "AESTHETIC 1/1" },
  { key: "street", label: "STREET STYLE" },
];

export default function Shop() {
  const { category = "all" } = useParams();
  const [searchParams] = useSearchParams();
  const initialQ = searchParams.get("q") || "";
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState(initialQ);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("new");
  const [selSizes, setSelSizes] = useState([]);
  const [selColors, setSelColors] = useState([]);
  const [priceMax, setPriceMax] = useState(5000);
  const [showFilters, setShowFilters] = useState(false);
  const [bargainOnly, setBargainOnly] = useState(false);

  // Sync search input with URL query changes (e.g. from navbar search)
  useEffect(() => { setQ(initialQ); }, [initialQ]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category && category !== "all") params.category = category;
    if (q) params.search = q;
    if (bargainOnly) params.bargain_only = true;
    api.get("/products", { params }).then(({ data }) => setProducts(data)).finally(() => setLoading(false));
  }, [category, q, bargainOnly]);

  const activeCat = CATEGORIES.find((c) => c.slug === category)?.label || "ALL";

  // Available filter options derived from current products
  const { allSizes, allColors, maxPrice } = useMemo(() => {
    const s = new Set(), c = new Set();
    let mp = 0;
    products.forEach((p) => {
      (p.sizes || []).forEach((x) => s.add(x));
      (p.colors || []).forEach((x) => c.add(x));
      if (p.price > mp) mp = p.price;
    });
    return { allSizes: [...s], allColors: [...c], maxPrice: Math.max(mp, 500) };
  }, [products]);

  useEffect(() => { setPriceMax(maxPrice); }, [maxPrice]);

  const filtered = useMemo(() => {
    let list = products.filter((p) => p.price <= priceMax);
    if (selSizes.length) list = list.filter((p) => (p.sizes || []).some((s) => selSizes.includes(s)));
    if (selColors.length) list = list.filter((p) => (p.colors || []).some((c) => selColors.includes(c)));
    if (sort === "price-asc") list = [...list].sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") list = [...list].sort((a, b) => b.price - a.price);
    else if (sort === "aesthetic") list = [...list].sort((a, b) => {
      const score = (p) => (p.tags || []).some((t) => /1 of 1|hand-painted|new/i.test(t)) ? 1 : 0;
      return score(b) - score(a);
    });
    else if (sort === "street") list = [...list].sort((a, b) => {
      const score = (p) => (["jackets", "tees", "jeans"].includes(p.category) ? 1 : 0) + ((p.tags || []).some((t) => /street|graffiti|custom/i.test(t)) ? 1 : 0);
      return score(b) - score(a);
    });
    // "new" is default (server already returns newest first)
    return list;
  }, [products, selSizes, selColors, priceMax, sort]);

  const toggle = (arr, setter, val) => setter(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  const clearFilters = () => { setSelSizes([]); setSelColors([]); setPriceMax(maxPrice); setSort("new"); };
  const activeFilterCount = selSizes.length + selColors.length + (priceMax < maxPrice ? 1 : 0) + (sort !== "new" ? 1 : 0);

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10" data-testid="shop-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <span className="sticker pink">SHOP</span>
          <h1 className="font-display text-6xl md:text-7xl mt-3">{activeCat}</h1>
        </div>
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" />
          <input
            data-testid="shop-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="SEARCH THE STREETS..."
            className="w-full pl-10 pr-4 py-3 border-2 border-black bg-white font-mono-b uppercase text-sm placeholder-black/50 brutal-shadow-sm focus:outline-none"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 border-y-2 border-black py-3 overflow-x-auto" data-testid="shop-filters">
        {CATEGORIES.map((c) => (
          <a
            key={c.slug}
            href={`/shop/${c.slug === "all" ? "" : c.slug}`}
            data-testid={`filter-${c.slug}`}
            className={`px-4 py-2 border-2 border-black text-sm font-mono-b font-bold uppercase hover-nudge ${category === c.slug ? "bg-black text-[#D4FF00]" : "bg-white"}`}
          >
            {c.label}
          </a>
        ))}
      </div>

      {/* Sort + Filter Toggle */}
      <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="shop-sort-row">
        <button
          data-testid="shop-toggle-filters"
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-4 py-2 border-2 border-black bg-white font-mono-b font-bold uppercase text-sm brutal-shadow-sm hover-nudge"
        >
          <SlidersHorizontal className="w-4 h-4" /> FILTERS
          {activeFilterCount > 0 && <span className="bg-[#FF007F] text-white w-5 h-5 flex items-center justify-center text-[10px] border-2 border-black rounded-full">{activeFilterCount}</span>}
        </button>

        <button
          type="button"
          data-testid="shop-bargain-toggle"
          onClick={() => setBargainOnly((b) => !b)}
          aria-pressed={bargainOnly}
          className={`inline-flex items-center gap-2 px-3 py-2 border-2 border-black font-mono-b font-bold uppercase text-xs brutal-shadow-sm hover-nudge ${bargainOnly ? "bg-[#D4FF00] text-black" : "bg-white"}`}
          title="Show only marketplace thrifts open to offers"
        >
          💬 OPEN TO OFFERS {bargainOnly && "✓"}
        </button>
        <div className="flex flex-wrap gap-1" data-testid="shop-sort">
          {SORTS.map((s) => (
            <button
              key={s.key}
              data-testid={`sort-${s.key}`}
              onClick={() => setSort(s.key)}
              className={`px-3 py-1 border-2 border-black text-xs font-mono-b font-bold uppercase ${sort === s.key ? "bg-[#D4FF00]" : "bg-white"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
        {activeFilterCount > 0 && (
          <button onClick={clearFilters} data-testid="shop-clear-filters" className="ml-auto text-xs font-mono-b underline font-bold flex items-center gap-1">
            <X className="w-3 h-3" /> CLEAR
          </button>
        )}
      </div>

      {showFilters && (
        <div className="bg-white border-2 border-black brutal-shadow p-5 mb-6 grid md:grid-cols-3 gap-5" data-testid="shop-filter-panel">
          {allSizes.length > 0 && (
            <div>
              <p className="font-mono-b font-bold uppercase text-xs mb-2">SIZE</p>
              <div className="flex flex-wrap gap-2">
                {allSizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`filter-size-${s}`}
                    onClick={() => toggle(selSizes, setSelSizes, s)}
                    className={`px-3 py-1 border-2 border-black font-mono-b font-bold text-xs ${selSizes.includes(s) ? "bg-black text-[#D4FF00]" : "bg-white"}`}
                  >{s}</button>
                ))}
              </div>
            </div>
          )}
          {allColors.length > 0 && (
            <div>
              <p className="font-mono-b font-bold uppercase text-xs mb-2">COLOR</p>
              <div className="flex flex-wrap gap-2">
                {allColors.map((c) => (
                  <button
                    key={c}
                    data-testid={`filter-color-${c}`}
                    onClick={() => toggle(selColors, setSelColors, c)}
                    className={`px-3 py-1 border-2 border-black font-mono-b font-bold text-xs ${selColors.includes(c) ? "bg-black text-[#D4FF00]" : "bg-white"}`}
                  >{c}</button>
                ))}
              </div>
            </div>
          )}
          <div>
            <p className="font-mono-b font-bold uppercase text-xs mb-2">MAX PRICE: <span className="text-[#FF007F]">₹{priceMax}</span></p>
            <input
              data-testid="filter-price"
              type="range"
              min={200}
              max={maxPrice}
              step={100}
              value={priceMax}
              onChange={(e) => setPriceMax(Number(e.target.value))}
              className="w-full accent-black"
            />
            <div className="flex justify-between text-xs font-mono-b mt-1"><span>₹200</span><span>₹{maxPrice}</span></div>
          </div>
        </div>
      )}

      {loading ? (
        <p className="font-mono-b" data-testid="shop-loading">LOADING FITS...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24 border-2 border-dashed border-black" data-testid="shop-empty">
          <p className="font-display text-3xl">NO FITS FOUND.</p>
          <p className="font-mono-b mt-2">Try another category or ease up on the filters.</p>
        </div>
      ) : (
        <>
          <p className="font-mono-b text-xs mb-3 text-black/60">{filtered.length} FIT{filtered.length !== 1 ? "S" : ""}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="shop-grid">
            {filtered.map((p) => <ProductCard key={p.product_id} product={p} />)}
          </div>
        </>
      )}
    </div>
  );
}
